import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Cookies from "js-cookie";

import { CommonButton } from "@/components/common/buttons/CommonButton";
import { EmptyResultMessage } from "@/components/atoms/EmptyResultMessage";
import Title from "@/components/molecules/Title";
import { AutoCompleteSearch } from "@/components/molecules/AutoCompleteSearch";
import { AutoCompleteMultiple } from "@/components/molecules/AutoCompleteMultiple";
import { TextFieldForSearch } from "@/components/molecules/SearchTextField";
import { VehicleList } from "./VehicleList";
import {
  VehicleConstants,
  UsingGxFilter,
  PAGE_SIZE,
  DefaultOrder,
} from "./VehicleConstants";
import {
  CarList,
  CarOrder,
  CarSortKey,
  GxFilter,
  REGISTERED,
  SavedState,
  SearchCarsCondition,
} from "@/models/vehicle";
import { Office, ONLY_CONTRACT } from "@/models/office";
import { getOfficesAPI } from "@/util/api/API";
import { getCarsAPI, getCarsTotalAPI } from "@/util/api/vehicle";
import { MULTI_OFFICE_ID } from "@/common/CommonConstants";
import { getOfficeOptionLabel } from "@/util";

export const VehiclePage = () => {
  const [searchCondition, setSearchCondition] =
    React.useState<SearchCarsCondition>({
      carFilter: REGISTERED,
    });

  const [total, setTotal] = React.useState<number>(0);
  const [page, setPage] = React.useState<number>(0);
  const [cars, setCars] = React.useState<CarList[]>([]);

  const [offices, setOffices] = React.useState<Office[]>([]);
  const [officesSelected, setOfficesSelected] = React.useState<Office[]>([]);

  const [vehicleCode, setVehicleCode] = React.useState("");

  const [gxFilterSelected, setGxFilterSelected] =
    React.useState<GxFilter | null>(UsingGxFilter[0]);

  const [order, setOrder] = React.useState<CarOrder>(DefaultOrder);
  const [noSerachResult, setNoSearchResult] = React.useState<boolean>(false);
  const [initSearchFlag, setInitSearchFlag] = React.useState<boolean>(false);
  const { state }: { state?: SavedState } = useLocation();

  // 指定された条件で車両一覧を取得・更新する
  const updateCarList = React.useCallback(
    (condition: SearchCarsCondition, page: number, order: CarOrder) => {
      getCarsTotalAPI(condition).then((res) => {
        setTotal(res.data?.count ?? 0);
        setPage(page);
      });
      getCarsAPI(condition, page, PAGE_SIZE, order).then((res) => {
        setCars(res.data ?? []);
        setNoSearchResult(!res.data.length ? true : false);
      });
    },
    []
  );

  const handleChangeOffices = React.useCallback(
    (offices: Office[]) => {
      setOfficesSelected(offices);
      setSearchCondition({
        ...searchCondition,
        officeIds: offices?.map(({ id }) => id) ?? [],
      });
      Cookies.set(MULTI_OFFICE_ID, offices.map(({ id }) => id).join(","));
    },
    [searchCondition]
  );

  const handleChangeVehicleCode = React.useCallback(
    (value: string) => {
      setVehicleCode(value);
      setSearchCondition({
        ...searchCondition,
        vehicleCode: value,
      });
    },
    [searchCondition]
  );

  const handleChangeUsingGx = React.useCallback(
    (e: React.SyntheticEvent<Element, Event>, filter: GxFilter | null) => {
      setGxFilterSelected(filter);
      setSearchCondition({
        ...searchCondition,
        carFilter: filter?.type,
      });
    },
    [searchCondition]
  );

  // Handle event change sort type
  const onClickChangeSort = (key: CarSortKey) => {
    const temp = {
      [key]: ["asc", undefined].includes(order[key]) ? "desc" : "asc",
    };

    updateCarList(searchCondition, 1, temp);

    setOrder(temp);
  };

  const handleOnPageChange = React.useCallback(
    (page: number) => {
      updateCarList(searchCondition, page, order);
    },
    [order, searchCondition, updateCarList]
  );

  // 検索条件に基づいて車両一覧をフィルタリングする
  const handleOnClickSearch = React.useCallback(() => {
    updateCarList(searchCondition, 1, order);
  }, [order, searchCondition, updateCarList]);

  // 検索条件をクリアする
  const handleOnClickReset = React.useCallback(() => {
    setOfficesSelected([]);
    setVehicleCode("");
    setGxFilterSelected(UsingGxFilter[0]);
    setSearchCondition({ carFilter: REGISTERED });
    setOrder(DefaultOrder);
  }, []);

  const navigate = useNavigate();

  // 画面遷移先に、検索条件を保存する
  const handleClickDetail = React.useCallback(
    (id: number) => {
      navigate(`/cars/${id}`, {
        state: {
          officesSelected,
          vehicleCode,
          page,
          order,
          searchCondition,
        },
      });
    },
    [navigate, officesSelected, order, page, searchCondition, vehicleCode]
  );

  const getOffices = React.useCallback(async () => {
    try {
      const res = await getOfficesAPI(ONLY_CONTRACT);
      setOffices(res.data || []);
      const founds = res.data.filter((d) =>
        Cookies.get(MULTI_OFFICE_ID)?.split(",").includes(d.id.toString())
      );
      if (founds.length) {
        setOfficesSelected(founds);
        setSearchCondition({
          ...searchCondition,
          officeIds: founds.map(({ id }) => id),
        });
        setInitSearchFlag(true);
      } else if (state) {
        setInitSearchFlag(true);
      }
    } catch (err) {
      console.log("==Error when get offices==", err);
    }
  }, [setOffices, searchCondition, state]);

  React.useEffect(() => {
    getOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // 保存された検索条件があれば、その検索条件をもとに車両一覧を取得する
    if (state != null && state.officesSelected != null && initSearchFlag) {
      setOfficesSelected(state.officesSelected);
      setVehicleCode(state.vehicleCode);
      setGxFilterSelected(
        UsingGxFilter.find(
          ({ type }) => type === state.searchCondition.carFilter
        ) ?? UsingGxFilter[0]
      );
      setPage(state.page);
      setOrder(state.order);
      setSearchCondition(state.searchCondition);
      updateCarList(state.searchCondition, state.page, state.order);
      // Cookieの営業所IDが営業所リストに存在した場合
    } else if (!state && initSearchFlag) {
      handleOnClickSearch();
      setInitSearchFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initSearchFlag, state]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>{VehicleConstants.Vehicle}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <AutoCompleteMultiple
                    options={offices}
                    limit={100}
                    forSearch
                    getOptionLabel={getOfficeOptionLabel}
                    value={officesSelected}
                    onChange={handleChangeOffices}
                    label={VehicleConstants.CompanyOffice}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextFieldForSearch
                    label={VehicleConstants.RadioNumber}
                    value={vehicleCode}
                    setValue={handleChangeVehicleCode}
                    sx={{ width: "100%" }}
                  />
                </Grid>
                <Grid item xs={2} xl={1.3}>
                  <AutoCompleteSearch
                    options={UsingGxFilter}
                    autoHighlight
                    getOptionLabel={(opt) => opt.display}
                    value={gxFilterSelected}
                    onChange={handleChangeUsingGx}
                    label={VehicleConstants.ChargingPlanSystem}
                    isOptionEqualToValue={(opt, value) =>
                      opt?.type === value.type
                    }
                  />
                </Grid>
                <Grid item xs={12} xl={2.7} container justifyContent="flex-end">
                  <CommonButton
                    btnType="Search"
                    onClickBtn={handleOnClickSearch}
                  />
                  <CommonButton
                    sx={{ ml: 2 }}
                    btnType="ResetSearchCriteria"
                    onClickBtn={handleOnClickReset}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <VehicleList
                onChangePage={handleOnPageChange}
                cars={cars}
                pagingData={{ page, total, size: PAGE_SIZE }}
                onClickChangeSort={onClickChangeSort}
                onClickDetail={handleClickDetail}
                orderTypes={order}
              />
              {noSerachResult && <EmptyResultMessage />}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
